import axios from 'axios'
import type { IEmployeeActivityItem } from '../types/IEmployeeActivityItem'
import type { IStaffDepartment } from '../types/IStaffDepartment'
import type { IEmployeeCreatePayload, IEmployeeRole, IEmployeeUpdatePayload, IStaffEmployee, TDismissalType } from '../types/IStaffEmployee'
import type { IStaffPosition } from '../types/IStaffPosition'
import type { IWidget } from '@/modules/dashboard/types/IWidget'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { BASE_URL } from '@/environment/environment'
import { onboardingApiUrl, onboardingEntityApiUrl } from '@/modules/onboarding/utils/demoApi'
import { toApiDate } from '@/utils/formatters'
export const useStaffStore = defineStore('staffStore', {
  state: () => ({
    staffList: [] as IStaffEmployee[],
    positionsList: [] as IStaffPosition[],
    departments: [] as IStaffDepartment[],
    currentDepartmentEmployees: [] as IStaffEmployee[],
    rolesList: [] as IEmployeeRole[],
    currentEmployee: null as IStaffEmployee | null,
    isLoading: true,
    nextCursor: null as string | null,
    totalItemsCount: 0,
    currentSortString: null as string | null,
    filters: {
      search: '',
      departments: [],
      positions: [],
      roles: [],
      status: [],
      last_login_after: null as Date | null,
      last_login_before: null as Date | null,
      /** true — только активные сотрудники (переключатель «Скрыть неактивных»), null — все */
      is_actual: null as boolean | null,
    },
    filterChoices: {} as Record<string, IFilterChoiceItem[]>,
    searchChoices: {} as Record<string, IFilterChoiceItem[]>,
    filterCursors: {} as Record<string, string | null>,
    filterLoading: {} as Record<string, boolean>,
    filterStrategies: {} as Record<string, string>,
    itemsPerPage: 25,
  }),

  actions: {
    /**
     * Загружает дерево отделений (структуру компании)
     */
    async fetchDepartments(search = '') {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const params = search.trim() ? { search: search.trim() } : undefined
        const res = await axios.get(onboardingApiUrl('employee', '/employee/management/department/'), { params })
        const data = res.data
        const rawResults = data?.results ?? data?.items ?? data ?? []
        this.departments = Array.isArray(rawResults) ? rawResults : []
      } catch (error) {
        console.error('❌ Ошибка загрузки структуры компании в staffStore:', error)
        this.departments = []
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает сотрудников конкретного отделения
     */
    async fetchDepartmentEmployees(departmentId: string) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const res = await axios.get(onboardingApiUrl('employee', `/employee/management/department/${departmentId}/employees/`))
        const raw = res.data?.results ?? res.data?.items ?? res.data ?? []
        this.currentDepartmentEmployees = Array.isArray(raw) ? raw : []
      } catch (error) {
        console.error(`❌ Ошибка загрузки сотрудников отделения ${departmentId} в staffStore:`, error)
        this.currentDepartmentEmployees = []
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает список доступных ролей с бэкенда.
     */
    async fetchRoles() {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const res = await axios.get(onboardingApiUrl('employee', '/employee/management/roles/'))
        this.rolesList = res.data?.result ?? []
      } catch (error) {
        console.error('❌ Ошибка загрузки ролей в staffStore:', error)
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает сотрудников с сервера с учетом фильтров, сортировки и пагинации
     */
    async fetchStaff(cursor?: string, itemsPerPage?: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      this.isLoading = true
      try {
        const params: Record<string, string | number | boolean | undefined> = {}

        if (this.currentSortString) {
          params.sort = this.currentSortString
        }

        const limit = itemsPerPage || this.itemsPerPage || 25
        this.itemsPerPage = limit
        params.page_size = limit

        const filters = this.filters

        if (filters.search) {
          params.search = filters.search
        }

        if (filters.departments.length) {
          params.departments = filters.departments.join(',')
        }

        if (filters.positions.length) {
          params.positions = filters.positions.join(',')
        }

        if (filters.roles.length) {
          params.roles = filters.roles.join(',')
        }

        if (filters.status.length) {
          params.status = filters.status.join(',')
        }

        if (filters.last_login_after) {
          params.last_login_after = toApiDate(filters.last_login_after) ?? undefined
        }

        if (filters.last_login_before) {
          params.last_login_before = toApiDate(filters.last_login_before) ?? undefined
        }

        if (filters.is_actual !== null) {
          params.is_actual = filters.is_actual
        }

        if (cursor) {
          params.cursor = cursor
        }

        const res = await axios.get(`${BASE_URL}/api/employee/management/`, { params })

        const rawResults = res.data.items ?? res.data.results ?? res.data
        const results = (Array.isArray(rawResults) ? rawResults : []) as IStaffEmployee[]

        const totalCount = res.data.pagination?.count ?? res.data.count ?? results.length
        this.totalItemsCount = totalCount

        this.nextCursor = res.data.next || null

        if (cursor) {
          this.staffList = [...this.staffList, ...results]
        } else {
          this.staffList = results
        }
      } catch (error) {
        console.error('❌ Ошибка загрузки сотрудников в fetchStaff:', error)
        feedbackStore.showToast({
          type: 'error',
          message: (error as any)?.response?.data?.errors?.[0]?.detail || 'Не удалось загрузить данные сотрудников',
          time: 5000,
        })
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Загружает должности (один раз при инициализации)
     */
    async fetchPositions() {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const { data } = await axios.get(`${BASE_URL}/api/employee/position/`)

        this.positionsList = data?.results ?? []
      } catch (error) {
        console.error('❌ Ошибка загрузки должностей в staffStore:', error)
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Получает список сотрудников конкретного подразделения (без изменения состояния таблицы)
     */
    async fetchEmployeesByDepartment(departmentId: string, itemsPerPage?: number): Promise<IStaffEmployee[]> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = { departments: departmentId }
        if (itemsPerPage !== undefined) {
          params.page_size = itemsPerPage
        }
        const response = await axios.get(onboardingApiUrl('employee', '/employee/management/'), { params })
        const rawResults = response.data.items ?? response.data.results ?? response.data
        return (Array.isArray(rawResults) ? rawResults : []) as IStaffEmployee[]
      } catch (error) {
        console.error('❌ Ошибка загрузки сотрудников подразделения:', error)
        return []
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async fetchLatestActions(employeeId: string, cursor?: string, pageSize?: number): Promise<any> {
      try {
        const params: Record<string, any> = {}
        if (cursor) {
          params.cursor = cursor
        }
        if (pageSize) {
          params.page_size = pageSize
        }
        const res = await axios.get(onboardingEntityApiUrl('employee', employeeId, `/employee/management/${employeeId}/latest-actions/`), { params })
        return res.data
      } catch (error) {
        console.error(`❌ Ошибка получения последних действий сотрудника ${employeeId}:`, error)
        throw error
      }
    },

    /**
     * Загружает сотрудников, подразделения и должности с бэкенда.
     * Оставляем для полной совместимости со старыми вызовами.
     */
    async loadStaff(searchQuery?: string) {
      if (this.positionsList.length === 0) {
        await this.fetchPositions()
      }

      if (searchQuery !== undefined) {
        this.filters.search = searchQuery
      }
      await this.fetchStaff()
    },

    /**
     * Загрузка вариантов фильтрации для UniversalFilterTree
     */
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        const params: Record<string, string | number | boolean | undefined> = { field: fieldKey }

        if (search) {
          params.search_field = search
        }

        if (cursor) {
          params.cursor = cursor
        }

        const filters = this.filters

        // Подмешиваем активные фильтры (контекст)
        if (fieldKey !== 'departments' && filters.departments.length) {
          params.departments = filters.departments.join(',')
        }
        if (fieldKey !== 'positions' && filters.positions.length) {
          params.positions = filters.positions.join(',')
        }
        if (fieldKey !== 'roles' && filters.roles.length) {
          params.roles = filters.roles.join(',')
        }
        if (fieldKey !== 'status' && filters.status.length) {
          params.status = filters.status.join(',')
        }
        if (filters.search) {
          params.search = filters.search
        }
        if (filters.last_login_after) {
          params.last_login_after = toApiDate(filters.last_login_after) ?? undefined
        }
        if (filters.last_login_before) {
          params.last_login_before = toApiDate(filters.last_login_before) ?? undefined
        }
        if (filters.is_actual !== null) {
          params.is_actual = filters.is_actual
        }

        const response = await axios.get(onboardingApiUrl('employee', '/employee/management/filter-choices/'), { params })
        const { next, strategy } = response.data
        const results = response.data.results

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          if (cursor) {
            this.searchChoices[fieldKey] = [...(this.searchChoices[fieldKey] || []), ...results]
          } else {
            this.searchChoices[fieldKey] = results
          }
        } else {
          if (cursor) {
            this.filterChoices[fieldKey] = [...(this.filterChoices[fieldKey] || []), ...results]
          } else {
            this.filterChoices[fieldKey] = results
          }
        }
        this.filterCursors[fieldKey] = next || null
      } catch (e) {
        console.error(`Ошибка загрузки фильтров сотрудников [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    resetFilters(): void {
      this.filters = {
        search: '',
        departments: [],
        positions: [],
        roles: [],
        status: [],
        last_login_after: null,
        last_login_before: null,
        // Переключатель «Скрыть неактивных» сбросом не трогается
        is_actual: this.filters.is_actual,
      }
      this.currentSortString = null
    },

    /**
     * Обновление данных сотрудника на сервере
     */
    async updateEmployee(id: string, payload: Partial<IEmployeeUpdatePayload>) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.patch(`${BASE_URL}/api/employee/management/${id}/`, payload)
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Массовое обновление ролей сотрудников
     */
    async bulkUpdateEmployeeRoles(employeeIds: string[], addRoleSubs: string[], deleteRoleSubs: string[]) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.post(`${BASE_URL}/api/employee/management/roles/`, {
          employee_ids: employeeIds,
          add_role_subs: addRoleSubs,
          delete_role_subs: deleteRoleSubs,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Блокирует сотрудника
     */
    async blockEmployee(id: string, payload: { reason: string; until: string | null }) {
      const feedbackStore = useFeedbackStore()

      try {
        await axios.post(`${BASE_URL}/api/employee/management/${id}/block/`, payload)

        // Заново загружаем текущую страницу списка сотрудников
        await this.fetchStaff(undefined, this.itemsPerPage)

        feedbackStore.showToast({
          type: 'success',
          message: 'Сотрудник заблокирован',
          time: 4000,
        })
      } catch (error) {
        console.error('❌ Ошибка блокировки сотрудника:', error)
        feedbackStore.showToast({
          type: 'error',
          message: (error as any)?.response?.data?.errors?.[0]?.detail || 'Не удалось заблокировать сотрудника',
          time: 5000,
        })
      }
    },

    /**
     * Увольняет сотрудника
     */
    async dismissEmployee(id: string, payload: { dismissalDate: string; dismissalType: TDismissalType; reason: string }) {
      const feedbackStore = useFeedbackStore()

      try {
        await this.updateEmployee(id, {
          dismissal_date: payload.dismissalDate,
          dismissal_type: payload.dismissalType,
          dismissal_reason: payload.reason,
          is_active: false,
        })

        // Заново загружаем текущую страницу списка сотрудников
        await this.fetchStaff(undefined, this.itemsPerPage)

        feedbackStore.showToast({
          type: 'success',
          message: 'Сотрудник уволен',
          time: 4000,
        })
      } catch (error) {
        console.error('❌ Ошибка увольнения сотрудника:', error)
        feedbackStore.showToast({
          type: 'error',
          message: (error as any)?.response?.data?.errors?.[0]?.detail || 'Не удалось уволить сотрудника',
          time: 5000,
        })
      }
    },

    /**
     * Восстанавливает сотрудника
     */
    async restoreEmployee(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()

      try {
        await axios.post(`${BASE_URL}/api/employee/management/${id}/unblock/`)

        // Заново загружаем текущую страницу списка сотрудников
        await this.fetchStaff(undefined, this.itemsPerPage)

        feedbackStore.showToast({
          type: 'success',
          message: 'Сотрудник восстановлен',
          time: 4000,
        })
        return true
      } catch (error) {
        console.error('❌ Ошибка восстановления сотрудника:', error)
        feedbackStore.showToast({
          type: 'error',
          message: (error as any)?.response?.data?.errors?.[0]?.detail || 'Не удалось восстановить сотрудника',
          time: 5000,
        })
        return false
      }
    },

    /**
     * Назначает роль сотруднику
     */
    async assignRole(id: string, roleSub: string) {
      const feedbackStore = useFeedbackStore()

      try {
        await this.updateEmployee(id, {
          roles: [{ sub: roleSub }],
        })

        // Заново загружаем текущую страницу списка сотрудников
        await this.fetchStaff(undefined, this.itemsPerPage)

        feedbackStore.showToast({
          type: 'success',
          message: 'Роль сотрудника обновлена',
          time: 4000,
        })
      } catch (error) {
        console.error('❌ Ошибка обновления роли сотрудника:', error)
        feedbackStore.showToast({
          type: 'error',
          message: (error as any)?.response?.data?.errors?.[0]?.detail || 'Не удалось обновить роль сотрудника',
          time: 5000,
        })
      }
    },

    /**
     * Добавляет нового сотрудника в список
     */
    async addEmployee(payload: IEmployeeCreatePayload) {
      const feedbackStore = useFeedbackStore()

      try {
        const createPayload = {
          last_name: payload.last_name,
          first_name: payload.first_name,
          middle_name: payload.middle_name,
          snils: payload.snils || null,
          phone_number: payload.phone_number || null,
          work_phone_number: payload.work_phone_number || null,
          email: payload.email || null,
          birth_date: payload.birth_date || null,
          hire_date: payload.hire_date || null,
          roles: payload.roles || [],
          position: payload.position,
          department: payload.department,
        }

        await axios.post(`${BASE_URL}/api/employee/management/`, createPayload)

        // После успешного создания заново загружаем текущую страницу списка сотрудников
        await this.fetchStaff(undefined, this.itemsPerPage)

        feedbackStore.showToast({
          type: 'success',
          message: 'Новый сотрудник добавлен!',
          time: 4000,
        })
      } catch (error) {
        console.error('❌ Ошибка добавления сотрудника:', error)
        feedbackStore.showToast({
          type: 'error',
          message: (error as any)?.response?.data?.errors?.[0]?.detail || 'Не удалось добавить сотрудника',
          time: 5000,
        })
        throw error
      }
    },

    /**
     * Редактирует сотрудника на сервере и заново загружает список.
     */
    async editEmployee(id: string, payload: IEmployeeUpdatePayload) {
      const feedbackStore = useFeedbackStore()

      try {
        await this.updateEmployee(id, payload)

        // После успешного редактирования заново загружаем текущую страницу списка сотрудников
        await this.fetchStaff(undefined, this.itemsPerPage)

        feedbackStore.showToast({
          type: 'success',
          message: 'Данные сотрудника обновлены!',
          time: 4000,
        })
      } catch (error) {
        console.error('❌ Ошибка обновления сотрудника:', error)
        feedbackStore.showToast({
          type: 'error',
          message: (error as any)?.response?.data?.errors?.[0]?.detail || 'Не удалось обновить данные сотрудника',
          time: 5000,
        })
        throw error
      }
    },

    /**
     * Удаляет сотрудника на сервере и локально из списков.
     */
    async deleteEmployee(id: string) {
      const feedbackStore = useFeedbackStore()

      try {
        await axios.delete(`${BASE_URL}/api/employee/management/${id}/`)

        // Заново загружаем текущую страницу списка сотрудников
        await this.fetchStaff(undefined, this.itemsPerPage)

        feedbackStore.showToast({
          type: 'success',
          message: 'Сотрудник успешно удален',
          time: 4000,
        })
      } catch (error) {
        console.error('❌ Ошибка удаления сотрудника:', error)
        feedbackStore.showToast({
          type: 'error',
          message: (error as any)?.response?.data?.errors?.[0]?.detail || 'Не удалось удалить сотрудника',
          time: 5000,
        })
      }
    },

    /**
     * Получает детальную информацию о сотруднике по ID
     */
    async fetchEmployeeById(id: string): Promise<IStaffEmployee | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const res = await axios.get(onboardingEntityApiUrl('employee', id, `/employee/management/${id}/`))
        this.currentEmployee = (res.data || null) as IStaffEmployee | null
        return this.currentEmployee
      } catch (error) {
        console.error('❌ Ошибка загрузки деталей сотрудника:', error)
        this.currentEmployee = null
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Получает последние действия сотрудника с сервера
     */
    async fetchEmployeeActions(id: string, cursor?: string, pageSize?: number): Promise<{ items: IEmployeeActivityItem[]; next: string | null }> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, string | number | undefined> = {}
        if (pageSize !== undefined) {
          params.page_size = pageSize
        }
        if (cursor) {
          params.cursor = cursor
        }
        const res = await axios.get(`${BASE_URL}/api/employee/management/actions/${id}/`, { params })
        return res.data
      } catch (error) {
        console.error('❌ Ошибка получения действий сотрудника:', error)
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Получает статистику активности сотрудника с сервера
     */
    async fetchEmployeeActivity(id: string, period?: string): Promise<IWidget[]> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const res = await axios.get<{ filters: any[]; widgets: IWidget[] }>(
          onboardingEntityApiUrl('employee', id, `/employee/management/${id}/activity/`),
          { params: period ? { period } : undefined },
        )
        return res.data.widgets || []
      } catch (error) {
        console.error('❌ Ошибка получения статистики активности сотрудника:', error)
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStaffStore, import.meta.hot))
}
